import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { StorageService } from '@metromarkets/sdk';
import { MmDialogService } from '@metromarkets/components';

import { AccountInfoService } from '~core/modules/identity/services/account-info.service';
import { ACCOUNT_CHANGE_EMAIL_LINK, ACCOUNT_INFORMATION_LINK } from '~shared/constants/links.constants';
import { IAccountInformation } from '~shared/models';
import { GeneralUtil } from '~shared/utils/general.util';
import {
  ExpiredChangeEmailRequestComponent
} from '../components/expired-change-email-request/expired-change-email-request.component';
import { FRAGMENT_EXPIRED_CHANGE_EMAIL_LINK } from '../constants/account-info.constant';
import {
  CHANGE_EMAIL_REQUESTS_KEY,
  CHANGE_EMAIL_LINK_DURATION,
} from '../constants/expired-change-email-request.constant';

@Injectable()
export class ExpiredChangeEmailRequestService implements OnDestroy {
  private accountInformation: IAccountInformation;
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private readonly dialogService: MmDialogService,
    private storageService: StorageService,
    private accountInfoService: AccountInfoService,
  ) {
    this.accountInfoService.getAccountInfo().pipe(
      takeUntil(this.destroy$)
    ).subscribe((accountInformation: IAccountInformation) => {
      this.accountInformation = accountInformation;
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public addRequest(): void {
    if (!this.accountInformation) {
      return;
    }

    const emailHash = GeneralUtil.toHash(this.accountInformation.email);
    const expirationDate = new Date();
    expirationDate.setHours(expirationDate.getHours() + CHANGE_EMAIL_LINK_DURATION);
    const expirationTime = expirationDate.getTime();

    const changeEmailRequests = this.storageService.local.get(CHANGE_EMAIL_REQUESTS_KEY) || {};
    this.storageService.local.set(CHANGE_EMAIL_REQUESTS_KEY, {
      ...changeEmailRequests,
      [this.accountInformation.id]: { [emailHash]: expirationTime },
    });
  }

  public checkExpiredRequest(): void {
    const fragment = this.route.snapshot.fragment;
    const isLinkExpired = fragment === FRAGMENT_EXPIRED_CHANGE_EMAIL_LINK;
    const isRequestExpired = this.hasExpiredRequest();

    if (!isLinkExpired && !isRequestExpired) {
      return;
    }

    this.dialogService.open(ExpiredChangeEmailRequestComponent)
      .afterClosed()
      .pipe(take(1))
      .subscribe(
      (isChangeEmailSelected: boolean) => {
        this.removeRequest();

        const link = isChangeEmailSelected ? ACCOUNT_CHANGE_EMAIL_LINK : ACCOUNT_INFORMATION_LINK;

        this.router.navigateByUrl(link);
      }
    );
  }

  private hasExpiredRequest(): boolean {
    if (!this.accountInformation) {
      return false;
    }

    const changeEmailRequests = this.storageService.local.get(CHANGE_EMAIL_REQUESTS_KEY) || {};
    const changeEmailRequest = changeEmailRequests[this.accountInformation.id];

    if (!changeEmailRequest) {
      return false;
    }

    const emailHash = GeneralUtil.toHash(this.accountInformation.email);
    const expirationTime = changeEmailRequest[emailHash];

    if (!expirationTime) {
      this.removeRequest();
      return false;
    }

    const currentDate = new Date();

    return currentDate.getTime() >  expirationTime;
  }

  private removeRequest(): void {
    if (!this.accountInformation) {
      return;
    }

    const changeEmailRequests = this.storageService.local.get(CHANGE_EMAIL_REQUESTS_KEY) || {};
    delete changeEmailRequests[this.accountInformation.id];
    this.storageService.local.set(CHANGE_EMAIL_REQUESTS_KEY, changeEmailRequests);
  }
}
